import {
  FileOutlined,
  GroupOutlined,
  LikeOutlined,
  MessageOutlined,
  SendOutlined,
  TagOutlined,
  UsergroupAddOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import React, { ReactNode } from 'react';
import { useBillingDetails } from '../use-billing-details';
import '../BillingPageCard.pcss';
import { SubscriptionTabDefinition } from '@mm/state/helpers/billing-subscription-tab-helpers';
import { UsagePercentageBar } from '../../../../../../../../common/components/UsagePercentageBar/UsagePercentageBar';
import { abbreviateNumber, centsToDollars } from '@mm/utils';
import classNames from 'classnames';
import { LevelChoosesTemplate } from '../LevelChoosesTemplate/LevelChoosesTemplate';
import { PlanInfo } from '@mm/state/helpers/billing-info-helpers';
import PromoCodeArea from '../PromoCodeArea/PromoCodeArea';
import OveragesManagement from '../../OveragesManagement/OveragesManagement';
import { CreditsLimitReachedWarning } from './CreditsLimitReachedWarning';
import { SeatsArea } from '../../SeatsArea/SeatsArea';
import { getShowTeamOverageArea, getUsedAndTotal } from '../../helpers';
import { isNumber, uniqueId } from 'lodash';
import { ReactComponent as LeadMagnet } from 'assets/lead-generation-magnet.svg';
import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';
import {
  AddonSettingsKeyType,
  ADDON_SETTINGS_KEYS,
  EnhancedPlan,
  PlanBillingPeriodBE,
} from '@mm/state/interfaces/subscription';

type Props = {
  page: EnhancedBillingPage;
  planTab: SubscriptionTabDefinition;
  loadPricesForNextLevel: (subscriptionId: number, level: number) => void;
  planInfoMemoised: Partial<PlanInfo>;
  onUpgradeClick?: () => void;
};

type BillingPageTableItemProps = {
  icon: JSX.Element;
  title: string | ReactNode;
  pageId: number;
  used?: number;
  total?: number | string;
  children?: ReactNode;
  price?: string | ReactNode;
  bordered?: boolean;
  type?: AddonSettingsKeyType;
  plan?: EnhancedPlan;
  shouldShowPricing?: boolean;
  overage?: number;
};

const BillingPageTableItem = (props: BillingPageTableItemProps) => {
  const {
    icon,
    title,
    used,
    total,
    children,
    price,
    bordered,
    type,
    plan,
    pageId,
    shouldShowPricing,
    overage,
  } = props;

  return (
    <div className={classNames('billing-page-card__table-item')}>
      <span className={classNames('billing-page-card__table-item-title')}>
        {icon}
        {title}
      </span>

      <span
        className={classNames('billing-page-card__table-item-used', {
          'billing-page-card__table-item-used-out-of-total':
            isNumber(used) && total,
          'billing-page-card__table-item-used-exceeds':
            (total && used && isNumber(used) ? used : 0) > (total || 0) &&
            shouldShowPricing,
        })}
      >
        {isNumber(used) ? abbreviateNumber(used) : ''}
      </span>

      <span
        className={classNames('billing-page-card__table-item-total', {
          'billing-page-card__item-bordered': bordered,
        })}
      >
        {isNumber(total) ? abbreviateNumber(total) : total}
      </span>

      <span className="billing-page-card__table-item-overages">
        {overage && overage > 0 ? overage : ''}
      </span>

      {type && plan && shouldShowPricing ? (
        <BillingPageItemPrice type={type} pageId={pageId} plan={plan} />
      ) : (
        <span />
      )}

      <span className="billing-page-card__table-item-price">{price || ''}</span>

      {children}
    </div>
  );
};

const BillingPageItemPrice = ({
  type,
  pageId,
  plan,
}: {
  type: AddonSettingsKeyType;
  pageId: number;
  plan: EnhancedPlan;
}) => {
  const { openBillingPriceTableModal } = useModalActions();
  return (
    <div
      className="billing-page-card__item-price"
      onClick={() => openBillingPriceTableModal(type, pageId, plan)}
    >
      View Pricing
    </div>
  );
};

const BillingUpgradeTag = ({
  title,
  onUpgradeClick,
}: {
  title: string;
  onUpgradeClick?: () => void;
}) => (
  <div className="billing-page-card__table-header-upgrade">
    <div className="billing-page-card__table-header-tag">
      <TagOutlined /> {title}
    </div>
    <span
      className="billing-page-card__table-header-upgrade-link"
      onClick={onUpgradeClick}
    >
      Upgrade
    </span>
  </div>
);
// tslint:disable-next-line:cyclomatic-complexity
export const BillingPageTable = (props: Props) => {
  const {
    page,
    planTab,
    loadPricesForNextLevel,
    planInfoMemoised,
    onUpgradeClick,
  } = props;
  const { messagesStatistic: statistics, subscription } = page || {};
  const [usedSends, totalSends] = getUsedAndTotal(statistics, subscription);
  const { openGeneralTextModal, openBillingModal } = useModalActions();
  const hasSubscriptions = !!page?.subscriptions?.length;
  const pageId = page?.id;

  const {
    currentSubscription,
    billingPeriod: period,
    subscriptionPrice,
    nextBillPlanName,
    thisPeriodBillCombinedInfo,
    seatsPrice,
    smsNumberPrice,
    additionalPagesPrice,
    thisPeriodSmsNumberQuantity,
    sendsPrice,
    subscriptionIsCanceled,
    subscriptionEndsAt,
    shouldShowCredits,
    canManageOverages,
    canManageExtraSends,
    isAllowedByLockStrategy,
    nextPeriodSeats,
    thisPeriodSeats,
    thisPeriodAdditionalPages,
  } = useBillingDetails(page, planInfoMemoised, planTab.teamSubscription);

  const customPeriodLength = currentSubscription?.planIntervalLength;
  const isCustomBillingPeriod = customPeriodLength && customPeriodLength > 1;

  if (!page) {
    return null;
  }

  const billingSubscriptionItems = [
    {
      icon: <LeadMagnet />,
      title: 'X-Ray Leads Captured',
      type: ADDON_SETTINGS_KEYS.xrayLeads,
    },
    {
      icon: <SendOutlined />,
      title: 'Email Sends',
      type: ADDON_SETTINGS_KEYS.emailSends,
    },
    {
      icon: <UsergroupAddOutlined />,
      title: 'Consumer Directory Leads',
      type: ADDON_SETTINGS_KEYS.mercuryLeads,
    },
    {
      icon: <LikeOutlined />,
      title: 'Social Sends',
      type: ADDON_SETTINGS_KEYS.socialSends,
    },
    {
      icon: <MessageOutlined />,
      title: 'SMS/MMS',
      type: ADDON_SETTINGS_KEYS.smsSends,
    },
  ];

  const shouldShowUpgrade = !(
    planInfoMemoised.isCustomTeam || planInfoMemoised.isSpecialTeam
  );

  const showTeamOverageArea = getShowTeamOverageArea(
    planTab.tabDetails.teamMessageStatistics,
    planTab.teamSubscription,
  );

  const teamCanManageOverages =
    planInfoMemoised.isAnyTeamPlan &&
    !!planTab?.plan?.outgoingMessagesLimit &&
    isAllowedByLockStrategy &&
    !showTeamOverageArea;

  const isBilling2023Plan =
    currentSubscription && isBilling2023Subscription(currentSubscription);

  const changeSeats = () => {
    if (
      (!page.subscription || !planTab.teamSubscription) &&
      !currentSubscription
    ) {
      return;
    }
    const seatsObject = {
      userSeats: currentSubscription?.userSeats || 0,
      extraUserSeats: currentSubscription?.extraUserSeats || 0,
    };
    if (planTab.plan) {
      openBillingModal({
        facebookPage: page,
        plan: planTab.plan,
        seatsObject,
        subscription: currentSubscription || page.subscription,
        isMonthly: planTab.plan.billingPeriod === PlanBillingPeriodBE.month,
        onSuccessBilling: () =>
          openGeneralTextModal({
            header: 'User seats changed',
            message: `All set! Available seats have been updated on your account. You can invite users to fill these seats on the User dashboard`,
          }),
      });
    }
  };

  return (
    <div className="billing-page-card__table">
      <div className="billing-page-card__table-header">
        <div>Service</div>
        {(!page.redemption ||
          page.redemption.freeTrial ||
          page.subscription) && (
          <>
            <div>Used</div>
            <div>Included {!isCustomBillingPeriod ? period : ''}</div>
          </>
        )}

        {hasSubscriptions && (
          <>
            <div>Overage</div>
            {shouldShowUpgrade && !subscriptionIsCanceled && (
              <BillingUpgradeTag
                title="Want to eliminate overages?"
                onUpgradeClick={onUpgradeClick}
              />
            )}
            <div />
          </>
        )}
      </div>

      <CreditsLimitReachedWarning
        page={page}
        planInfo={planInfoMemoised}
        planTab={planTab}
      />

      <BillingPageTableItem
        icon={<FileOutlined />}
        title={
          <span className="billing-page-card__table-item-plan">
            {nextBillPlanName
              ? nextBillPlanName
              : page.redemption
              ? 'Promotions'
              : 'Free'}{' '}
            <span style={{ textTransform: 'capitalize' }}>Plan</span>
          </span>
        }
        total={
          isCustomBillingPeriod
            ? `${currentSubscription?.planIntervalLength} ${period} period`
            : undefined
        }
        price={
          page.redemption && !page.subscriptions?.length ? (
            <PromoCodeArea page={page} />
          ) : (
            subscriptionPrice
          )
        }
        pageId={pageId}
      />

      {shouldShowCredits && !isBilling2023Plan && (
        <BillingPageTableItem
          icon={<SendOutlined />}
          title="Credits"
          price={sendsPrice}
          used={usedSends}
          total={totalSends}
          bordered={hasSubscriptions && !subscriptionIsCanceled}
          pageId={pageId}
        >
          <div className="billing-page-card__table-item-children">
            <UsagePercentageBar
              className={classNames('billing-page-card__percentage-bar', {
                'billing-page-card__percentage-bar-bordered':
                  hasSubscriptions && !subscriptionIsCanceled,
              })}
              fbPage={page}
              isTeam={planInfoMemoised.isAnyTeamPlan}
              isCustomTeam={planInfoMemoised.isCustomTeam}
            />
            <div className="billing-page-card__table-item-credits-managment">
              {canManageExtraSends && (
                <LevelChoosesTemplate
                  page={page}
                  planTab={planTab}
                  loadPricesForNextLevel={loadPricesForNextLevel}
                  planInfoMemoised={planInfoMemoised}
                />
              )}
              {(canManageOverages || teamCanManageOverages) && (
                <OveragesManagement
                  facebookPage={page}
                  subscription={currentSubscription}
                >
                  <a
                    className="send-limit-alert-message__overage-management-link"
                    href="https://mobilemonkey.com/help/article/03nc6a7axs-send-overage-automatic-upgrades"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Pricing
                  </a>
                </OveragesManagement>
              )}
            </div>
          </div>
        </BillingPageTableItem>
      )}

      {isBilling2023Plan &&
        billingSubscriptionItems.map(item => {
          const addonUsageByType = currentSubscription?.addonUsage?.[item.type];
          const shouldShowPrice =
            addonUsageByType && Number(addonUsageByType?.used) > 0;
          const included = addonUsageByType?.included;

          return (
            <BillingPageTableItem
              key={uniqueId()}
              icon={item.icon}
              title={item.title}
              bordered
              price={
                shouldShowPrice
                  ? `$${centsToDollars(Number(addonUsageByType?.cost))}`
                  : ''
              }
              used={addonUsageByType?.used}
              total={Number(included)}
              type={item.type}
              plan={planTab.plan}
              pageId={pageId}
              shouldShowPricing={!!addonUsageByType}
              overage={(addonUsageByType?.used || 0) - Number(included)}
            />
          );
        })}

      {(currentSubscription?.userSeats ||
        (currentSubscription && !!nextPeriodSeats)) && (
        <BillingPageTableItem
          icon={<UserOutlined />}
          title="User Seats"
          price={seatsPrice}
          used={currentSubscription?.userSeatsUsed}
          total={
            (currentSubscription?.userSeats || 0) - (thisPeriodSeats || 0) ||
            undefined
          }
          pageId={pageId}
          overage={thisPeriodSeats}
        >
          <div className="billing-page-card__seats">
            <SeatsArea
              changeSeats={changeSeats}
              subscription={currentSubscription}
            />
          </div>
        </BillingPageTableItem>
      )}

      {smsNumberPrice && (
        <BillingPageTableItem
          icon={<MessageOutlined />}
          title="SMS Numbers"
          price={smsNumberPrice}
          used={thisPeriodSmsNumberQuantity}
          pageId={pageId}
        />
      )}

      {additionalPagesPrice && (
        <BillingPageTableItem
          icon={<GroupOutlined />}
          title="Additional Pages"
          price={additionalPagesPrice}
          used={planTab.pages.length}
          total={
            (currentSubscription?.pagesLimit || 0) -
              (thisPeriodAdditionalPages || 0) || undefined
          }
          pageId={pageId}
          overage={thisPeriodAdditionalPages}
        />
      )}

      {page.subscription && (
        <div className="billing-page-card__table-total-wrapper">
          {!subscriptionIsCanceled ? (
            <div className="billing-page-card__table-total">
              {' '}
              <span className="billing-page-card__table-item-title">
                Total:
              </span>
              <span className="billing-page-card__table-item-title">
                {thisPeriodBillCombinedInfo}/
                {customPeriodLength
                  ? ` ${customPeriodLength} ${period}`
                  : `${period}`}
              </span>
            </div>
          ) : (
            <div className="billing-page-card__table-item-title">
              Plan Expires at {subscriptionEndsAt}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
